import { useCallback, useEffect, useState } from "react";
import { Plus, Search, LayoutGrid, Rows3, Receipt, GitBranch, MapPinned, Loader2, Map as MapIcon, ListOrdered, Columns3, DownloadCloud, Landmark } from "lucide-react";
import StickerPrintMenu from "@/components/stickers/StickerPrintMenu";
import AddColumnForm from "@/components/admin/AddColumnForm";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import RouteMapPanel from "@/components/routes/RouteMapPanel";
import { useNavigate } from "react-router-dom";
import { looksLikeContacts } from "@/lib/contact";
import CardsView from "@/components/CardsView";
import InvoicePreview from "@/components/InvoicePreview";
import RecordDetail from "@/components/RecordDetail";
import RenameColumnDialog from "@/components/RenameColumnDialog";
import BatchInvoiceDialog from "@/components/invoices/BatchInvoiceDialog";
import DateFilter from "@/components/DateFilter";
import { SubsetDialog, CopyRecordDialog, ImportRecordsDialog } from "@/components/SubDatabase";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, formatError, tablePath } from "@/lib/api";
import { useLiveRefresh } from "@/lib/realtime";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ExportMenu from "@/components/ExportMenu";
import DataGrid from "@/components/DataGrid";
import Pager from "@/components/Pager";
import RecordDialog from "@/components/RecordDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import SelectionBar from "@/components/SelectionBar";
import SetRouteDialog from "@/components/SetRouteDialog";
import MarkPaidDialog from "@/components/MarkPaidDialog";

export default function TableView({ db, table, onChange, databases, onSubCreated }) {
  const navigate = useNavigate();
  const { isAdmin, can } = useAuth();
  const [subOpen, setSubOpen] = useState(false);
  const [copying, setCopying] = useState(null);
  const base = tablePath(db.id, table.name);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState({ col: null, order: "asc" });
  const [res, setRes] = useState({ records: [], total: 0 });
  const [scanCounts, setScanCounts] = useState(null);
  const [qboSynced, setQboSynced] = useState(null);
  const [qboFilter, setQboFilter] = useState(false);
  const readOnly = table.name === "Customer Scans";
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [view, setView] = useState(() => (looksLikeContacts(table.columns) ? "cards" : "grid"));
  const [dateF, setDateF] = useState({ field: "", from: "", to: "" });
  const [selected, setSelected] = useState(new Map());
  const [subIds, setSubIds] = useState(null);
  const [sums, setSums] = useState(null);
  const isSub = !!db.parent_db_id;
  const [bulkDel, setBulkDel] = useState(false);
  const dateKey = `date-cols:${base}`;
  const [extraDates, setExtraDates] = useState(() => JSON.parse(localStorage.getItem(dateKey) || "[]"));
  useEffect(() => { setExtraDates(JSON.parse(localStorage.getItem(dateKey) || "[]")); }, [dateKey]);
  const applyDateCol = (col) => {
    if (!extraDates.includes(col)) {
      const next = [...extraDates, col];
      localStorage.setItem(dateKey, JSON.stringify(next));
      setExtraDates(next);
    }
    setDateF({ field: col, from: "", to: "" });
    setPage(1);
    toast.success(`${col} added as a date filter`);
  };

  useEffect(() => { setSelected(new Map()); }, [base, query, dateF]);
  const toggleOne = (id) => setSelected((s) => { const n = new Map(s); n.has(id) ? n.delete(id) : n.set(id, res.records.find((r) => r.id === id)); return n; });
  const togglePage = (on) => setSelected((s) => { const n = new Map(s); res.records.forEach((r) => (on ? n.set(r.id, r) : n.delete(r.id))); return n; });
  const paidCol = table.columns.some((c) => /^date\s*pd$|date\s*paid|^paid\s*date$/i.test(c.name.trim()));
  const routeCol = table.columns.some((c) => /^\s*(route|rt|route\s*#|route\s*no\.?)\s*$/i.test(c.name));
  const [routeIds, setRouteIds] = useState(null);
  const [paying, setPaying] = useState(false);
  const [mapStops, setMapStops] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const jobCol = table.columns.find((c) => /^\s*job\s*(#|no\.?|num(ber)?)?\s*$/i.test(c.name))?.name;
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const saveJobOrder = async () => {
    if (!window.confirm(`Renumber ${jobCol} 1 to ${mapStops.length} on these ${mapStops.length} records in the new map order?`)) return;
    setSavingOrder(true);
    try {
      const { data } = await api.post(`${base}/records/job-order`, { ids: mapStops.map((s) => s.record_id) });
      setMapStops((p) => p.map((s, i) => ({ ...s, job: String(i + 1) })));
      setOrderDirty(false);
      toast.success(`Saved new order: ${data.updated} records renumbered in ${data.column}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    setSavingOrder(false);
  };
  const collectIds = async () => {
    let ids = [...selected.keys()];
    if (ids.length) return ids;
    for (let p = 1; p <= 10; p += 1) {
      const { data } = await api.get(`${base}/records`, {
        params: {
          search: query || undefined, page: p, page_size: 200, sort: sort.col || undefined, order: sort.order,
          date_field: dateF.field || undefined, date_from: dateF.field && dateF.from ? dateF.from.slice(0, 10) : undefined,
          date_to: dateF.field && dateF.to ? dateF.to.slice(0, 10) : undefined,
        },
      });
      ids = ids.concat(data.records.map((x) => x.id));
      if (ids.length >= data.total || !data.records.length) break;
    }
    return ids;
  };
  const printInvoices = async () => {
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.post("/invoices/for-records", { db_id: db.id, table: table.name, ids: [...selected.keys()] });
      if (!data.ids.length) { tab.close(); toast.error("None of the selected records have an invoice yet"); return; }
      if (data.missing) toast.warning(`${data.missing} selected record${data.missing === 1 ? " has" : "s have"} no invoice and ${data.missing === 1 ? "was" : "were"} skipped`);
      localStorage.setItem("print-selected-ids", JSON.stringify(data.ids));
      tab.location.href = "/invoices/print-list?selected=1";
    } catch (e) {
      tab.close();
      toast.error(formatError(e));
    }
  };
  const printStickers = async () => {
    const tab = window.open("", "_blank");
    try {
      const ids = await collectIds();
      localStorage.setItem("sticker-print", JSON.stringify(ids.map((id) => ({ db_id: db.id, table: table.name, record_id: id }))));
      tab.location.href = "/stickers";
    } catch (e) {
      tab.close();
      toast.error(formatError(e));
    }
  };
  const toggleMap = async () => {
    setOrderDirty(false);
    if (mapStops) { setMapStops(null); return; }
    setMapLoading(true);
    try {
      const ids = await collectIds();
      const { data } = await api.post("/route-setup/stops-from", { db_id: db.id, table: table.name, ids });
      setMapStops(data.stops);
    } catch (e) {
      toast.error(formatError(e));
    }
    setMapLoading(false);
  };
  const [reopening, setReopening] = useState(false);
  const createRoute = async () => {
    try {
      const { data } = await api.post("/route-setup/stops-from", { db_id: db.id, table: table.name, ids: [...selected.keys()] });
      navigate("/routes", { state: { stops: data.stops } });
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const reopen = async () => {
    try {
      const { data } = await api.post("/invoices/reopen", { ids: [...selected.keys()] });
      toast.success(`${data.reopened} invoice${data.reopened === 1 ? "" : "s"} sent back to unpaid${data.skipped ? ` · ${data.skipped} skipped (no matching invoice)` : ""}`);
      setSelected(new Map());
      load();
      onChange();
    } catch (e) {
      toast.error(formatError(e));
    }
    setReopening(false);
  };
  const markPaid = async (date, label) => {
    try {
      const { data } = await api.post(`${base}/records/bulk-paid`, { ids: [...selected.keys()], date });
      toast.success(`${data.updated} record${data.updated === 1 ? "" : "s"}: ${data.column} set to ${label}`);
      setSelected(new Map());
      setPaying(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const sendQbo = async () => {
    try {
      const { data } = await api.post("/qbo/send-records", { db_id: db.id, table: table.name, ids: [...selected.keys()] });
      if (data.sent) toast.success(`${data.sent} invoice${data.sent === 1 ? "" : "s"} sent to QuickBooks`);
      const bad = (data.results || []).filter((r) => !r.ok);
      if (bad.length) toast.error(`${bad.length} failed: ${bad[0].error}`);
      if (data.missing) toast.warning(`${data.missing} selected record${data.missing === 1 ? " has" : "s have"} no linked invoice and ${data.missing === 1 ? "was" : "were"} skipped`);
      if (!data.sent && !bad.length) toast.message("Nothing to send to QuickBooks");
      if (data.sent) setSelected(new Map());
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const deleteSelected = async () => {
    try {
      const { data } = await api.post(`${base}/records/bulk-delete`, { ids: [...selected.keys()] });
      toast.success(`${data.deleted} record${data.deleted === 1 ? "" : "s"} deleted`);
      setSelected(new Map());
      load();
      onChange();
    } catch (e) {
      toast.error(formatError(e));
    }
    setBulkDel(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/records`, {
        params: {
          search: query || undefined, page, page_size: pageSize, sort: sort.col || undefined, order: sort.order,
          date_field: dateF.field || undefined, date_from: dateF.field && dateF.from ? dateF.from.slice(0, 10) : undefined,
          date_to: dateF.field && dateF.to ? dateF.to.slice(0, 10) : undefined,
          qbo: db.paid_archive && qboFilter ? "unsynced" : undefined,
        },
      });
      setRes(data);
      if (data.records.length && table.name !== "Customer Scans") {
        api.post(`${base}/scan-counts`, { ids: data.records.map((x) => x.id) })
          .then((c) => setScanCounts(c.data.column ? c.data.counts : null)).catch(() => setScanCounts(null));
      } else setScanCounts(null);
      if (data.records.length && db.paid_archive) {
        api.post(`/qbo/record-status`, { db_id: db.id, table: table.name, ids: data.records.map((x) => x.id) })
          .then((c) => setQboSynced(c.data.synced || {})).catch(() => setQboSynced(null));
      } else setQboSynced(null);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [base, query, page, pageSize, sort, dateF, table.name, qboFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load);

  // Sub-databases show a combined money summary (sum of Subtotal/Tax/Total/Parts/+Total) across all records.
  useEffect(() => {
    if (!isSub) { setSums(null); return; }
    api.get(`${base}/sums`).then((r) => setSums(r.data)).catch(() => setSums(null));
  }, [base, isSub, res.total]);

  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, order: s.order === "asc" ? "desc" : "asc" } : { col, order: "asc" }));

  const saved = () => { setEditing(null); load(); onChange(); };

  const cellSave = async (record, colName, value) => {
    const { data } = await api.put(`${base}/records/${record.id}`, { data: { [colName]: value } });
    setRes((s) => ({ ...s, records: s.records.map((r) => (r.id === record.id ? { ...r, data: data.data } : r)) }));
    toast.success("Saved");
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`${base}/records/${toDelete.id}`);
      toast.success("Record deleted");
      load();
      onChange();
    } catch (e) {
      toast.error(formatError(e));
    }
    setToDelete(null);
  };

  return (
    <div className="fade-up flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 truncate">{db.name}</p>
          <h1 className="text-3xl font-bold tracking-tight mt-1" data-testid="table-title">{table.name}</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="table-total">{res.total.toLocaleString()} records · {table.columns.length} fields</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu path={`${base}/export`} formats={["csv", "xlsx", "json", "accdb", "wdb"]} name={table.name} testId="export-button" />
          {!readOnly && <>
            {can("routes") && <Button variant="outline" onClick={createRoute} disabled={!table.row_count} data-testid="create-route-button" className="hover:bg-secondary hover:text-foreground">
              <MapPinned size={16} /> Create route{selected.size ? ` (${selected.size})` : ""}</Button>}
            {isAdmin && <Popover>
              <PopoverTrigger asChild><Button variant="outline" data-testid="add-column-button" className="hover:bg-secondary hover:text-foreground"><Columns3 size={16} /> Add column</Button></PopoverTrigger>
              <PopoverContent align="end" className="w-[420px] bg-card space-y-2" data-testid="add-column-popover">
                <p className="text-sm font-medium">Add a column to {table.name}</p>
                <AddColumnForm dbId={db.id} table={table.name} onAdded={() => { onChange(); load(); }} testPrefix="table-add-col" />
                <p className="text-xs text-muted-foreground">Remove or rename columns in Admin panel → Databases.</p>
              </PopoverContent>
            </Popover>}
            {isAdmin && <Button variant="outline" onClick={() => setSubOpen(true)} data-testid="sub-database-button" className="hover:bg-secondary hover:text-foreground"><GitBranch size={16} /> Sub database</Button>}
            {can("invoices") && <Button variant="outline" onClick={() => setBatchOpen(true)} disabled={!table.row_count} data-testid="invoice-all-button" className="hover:bg-secondary hover:text-foreground"><Receipt size={16} /> Invoice all</Button>}
            {isAdmin && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-records-button" className="hover:bg-secondary hover:text-foreground"><DownloadCloud size={16} /> Add from database</Button>}
            <Button onClick={() => setEditing({})} className="hover:bg-blue-600 hover:text-white" data-testid="add-record-button"><Plus size={16} /> Add record</Button>
          </>}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Contains… (searches every field)" className="pl-9" data-testid="table-search-input" />
        </div>
        <div className="inline-flex rounded-md border p-0.5 bg-card" data-testid="view-toggle">
          {[["cards", LayoutGrid, "Cards"], ["grid", Rows3, "Table"]].map(([k, Icon, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}-button`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors ${view === k ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {db.paid_archive && (
          <Button variant={qboFilter ? "secondary" : "outline"} size="sm" onClick={() => { setQboFilter((v) => !v); setPage(1); }} data-testid="qbo-filter-toggle" className="hover:bg-secondary hover:text-foreground">
            <Landmark size={15} /> {qboFilter ? "Showing: not in QuickBooks" : "Only not in QuickBooks"}
          </Button>
        )}
      </div>
      {isSub && sums && Object.keys(sums.sums || {}).length > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm" data-testid="subdb-totals">
          <span className="font-medium text-emerald-300">Combined totals</span>
          <span className="text-muted-foreground">{sums.count} record{sums.count === 1 ? "" : "s"}</span>
          {Object.entries(sums.sums).map(([col, amt]) => (
            <span key={col} data-testid={`subdb-total-${col.replace(/[^a-zA-Z]/g, "")}`}>
              <span className="text-muted-foreground">{col}: </span>
              <span className="font-mono font-semibold">${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </span>
          ))}
        </div>
      )}
      {table.name === "Customer Scans" && (
        <p className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-sm" data-testid="scans-table-notice">
          This table lists every customer scan and updates itself whenever scans are added, filed or deleted. Edits made here are replaced on the next update — manage scans from the Customers tab.
        </p>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateFilter columns={table.columns} value={dateF} extra={extraDates} onChange={(v) => { setDateF(v); setPage(1); }} />
        {table.name !== "Customer Scans" && (
          <div className="flex gap-2">
            {table.columns.some((c) => /^sticker/i.test(c.name.trim())) && (
              <StickerPrintMenu count={selected.size} disabled={!res.total || mapLoading} onPrint={printStickers} />
            )}
            <Button variant={mapStops ? "secondary" : "outline"} onClick={toggleMap} disabled={!res.total || mapLoading} data-testid="table-map-toggle" className="hover:bg-secondary hover:text-foreground">
              {mapLoading ? <Loader2 size={16} className="animate-spin" /> : <MapIcon size={16} />} {mapStops ? "Hide route map" : `Show route map${selected.size ? ` (${selected.size})` : ""}`}
            </Button>
          </div>
        )}
      </div>
      {mapStops && mapStops.length > 0 && jobCol && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="map-order-bar">
          <span className="text-muted-foreground">{orderDirty ? "Stop order changed on the map." : "Reorder stops on the map, then save the order to Job #."}</span>
          <Button size="sm" onClick={saveJobOrder} disabled={!orderDirty || savingOrder} data-testid="map-save-job-order">
            {savingOrder ? <Loader2 size={14} className="animate-spin" /> : <ListOrdered size={14} />} Save order to {jobCol}
          </Button>
        </div>
      )}
      {mapStops && mapStops.length > 0 && <RouteMapPanel stops={mapStops} onReorder={jobCol ? (s) => { setMapStops(s); setOrderDirty(true); } : undefined} />}
      {view === "grid" && !readOnly && <SelectionBar count={selected.size} isAdmin={isAdmin} canDelete={can("bulk_delete")} onCopy={() => setCopying([...selected.values()])} onSub={() => setSubIds([...selected.keys()])}
        onDelete={() => setBulkDel(true)} onClear={() => setSelected(new Map())} onMarkPaid={paidCol && !db.paid_archive ? () => setPaying(true) : null}
        onReopen={db.paid_archive ? () => setReopening(true) : null}
        onSendQbo={db.paid_archive ? sendQbo : null}
        onPrintInvoices={printInvoices}
        onSetRoute={routeCol ? () => setRouteIds([...selected.keys()]) : null} />}
      <ConfirmDialog open={reopening} onOpenChange={setReopening} onConfirm={reopen}
        title={`Send ${selected.size} invoice${selected.size === 1 ? "" : "s"} back to unpaid?`}
        description="They are taken out of Paid Invoices, show on the Invoices page again as unpaid, and the invoice # goes back on the customer's main record." testId="reopen-paid" confirmLabel="Send back to unpaid" />
      <MarkPaidDialog open={paying} count={selected.size} what="record" onClose={() => setPaying(false)} onConfirm={markPaid} />
      <SetRouteDialog ids={routeIds} base={base} onClose={() => setRouteIds(null)} onDone={() => { setRouteIds(null); setSelected(new Map()); load(); }} />
      {view === "cards" ? (
        <CardsView columns={table.columns} records={res.records} loading={loading} onOpen={setPreview} base={base} qboSynced={qboSynced} />
      ) : (
        <DataGrid readOnly={readOnly} columns={table.columns} records={res.records} loading={loading} sort={sort} onSort={toggleSort}
          onEdit={setEditing} onDelete={setToDelete} onView={setViewing} onRename={setRenaming} onCopy={(r) => setCopying([r])} offset={(page - 1) * pageSize}
          selected={selected} onToggle={toggleOne} onTogglePage={togglePage} onDateFilter={applyDateCol} dateField={dateF.field} scanCounts={scanCounts} qboSynced={qboSynced}
          editable={isAdmin && !readOnly} onCellSave={cellSave} />
      )}
      <Pager page={page} pageSize={pageSize} total={res.total} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
      <RecordDetail record={viewing} columns={table.columns} base={base} dbId={db.id} tableName={table.name} onClose={() => setViewing(null)}
        onEdit={(r) => { setViewing(null); setEditing(r); }} onScansChanged={() => { load(); onChange(); }} />
      <InvoicePreview record={preview} columns={table.columns} dbId={db.id} table={table.name} onClose={() => setPreview(null)} onOpenCard={() => { const r = preview; setPreview(null); setEditing(r); }} />
      <RecordDialog open={!!editing} record={editing} columns={table.columns} base={base} onClose={() => setEditing(null)} onSaved={saved} />
      <SubsetDialog open={subOpen || !!subIds} onClose={() => { setSubOpen(false); setSubIds(null); }} db={db} table={table} base={base} total={res.total} recordIds={subIds}
        filters={{ search: query || null, date_field: dateF.field || null, date_from: dateF.field && dateF.from ? dateF.from.slice(0, 10) : null, date_to: dateF.field && dateF.to ? dateF.to.slice(0, 10) : null }}
        onCreated={(d) => { setSubOpen(false); setSubIds(null); setSelected(new Map()); onSubCreated(d); }} />
      <CopyRecordDialog records={copying} onClose={() => setCopying(null)} databases={databases} dbId={db.id} base={base} onCopied={() => { setCopying(null); onChange(); }} />
      <ImportRecordsDialog open={importOpen} onClose={() => setImportOpen(false)} databases={databases} dbId={db.id} table={table} onImported={() => { onChange(); load(); }} />
      <BatchInvoiceDialog open={batchOpen} onClose={() => setBatchOpen(false)} db={db} table={table} base={base} />
      <RenameColumnDialog column={renaming} base={base} onClose={() => setRenaming(null)}
        onRenamed={() => { setRenaming(null); setSort({ col: null, order: "asc" }); onChange(); }} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={confirmDelete}
        title="Delete this record?" description="This record will be permanently removed." testId="delete-record" />
      <ConfirmDialog open={bulkDel} onOpenChange={setBulkDel} onConfirm={deleteSelected}
        title={`Delete ${selected.size} record${selected.size === 1 ? "" : "s"}?`} description="The selected records will be permanently removed." testId="bulk-delete-record" />
    </div>
  );
}
